import Calendar from '@/components/calendar/Calendar';
import { Button } from '@/components/common/Button';
import { Checkbox } from '@/components/common/Checkbox';
import Combobox from '@/components/common/Combobox';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/common/DropdownMenu';
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/common/Form';
import { Input } from '@/components/common/Input';
import { Label } from '@/components/common/Label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/common/Select';
import { ActionDropdownItem } from '@/components/general/ActionDropdownItem';
import Table from '@/components/table/Table';
import { COMMENT_IN_REVIEW_STATUS, DATE_YMD, DATE_YMD_HMS_12H_FORMAT, DEFAULT_SELECT_PAGE_SIZES, ROUTES, SHOP_RATING, USER_STATUS } from '@/configs/constants';
import { useStore } from '@/hooks/useStore';
import yup from '@/services/yup';
import { formatDateTime } from '@/utils/datetime';
import { toastify } from '@/utils/toastify';
import { convertServicePrice, formatNumber, scrollToTop } from '@/utils/utils';
import { yupResolver } from '@hookform/resolvers/yup';
import { ColumnDef, TableState, Updater } from '@tanstack/react-table';
import { Ban, Check, Copy, Edit, EllipsisIcon, PlusIcon, SearchIcon, Trash2Icon } from 'lucide-react';
import { flowResult, toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { LANGUAGES_SUPPORTED } from '@/types/enums';
import PetOwnerListPopup from './components/PetOwnerListPopup';

export default observer(function PromotionManagementPage() {
    const { t, i18n: { language } } = useTranslation();
    const navigate = useNavigate();

    const {
        modalStore: { showAlertModal, showModal, hideModal },
        promotionStore: { shopsSearchPromotion, getAllShopSearchPromotion, promotions, getAllPromotion, paging, totalRecord, setObservable, keepSearchParams, defaultPaging }
    } = useStore();

    const [searchState, setSearchState] = useState<Object>(keepSearchParams ?? '');

    // validate schema
    const validateSchema = yup.object().shape({
        promotionName: yup
            .string(),
        promotionCode: yup
            .string(),
        shopName: yup
            .string(),
        shopId: yup
            .number().nullable(),
        isAllShop: yup
            .boolean(),
        fromCreatedDate: yup
            .lazy((value) => {
                if (value === '') {
                    return yup.string().oneOf(['']);
                } else {
                    return yup.date();
                }
            }),
        toCreatedDate: yup
            .lazy((value) => {
                if (value === '') {
                    return yup.string().oneOf(['']);
                } else {
                    return yup.date();
                }
            }),
        status: yup
            .string()
    });

    type FormData = yup.InferType<typeof validateSchema>;

    const initialValues = {
        promotionName: '',
        promotionCode: '',
        shopName: '',
        shopId: null,
        isAllShop: false,
        fromCreatedDate: '',
        toCreatedDate: '',
        status: 'all'
    };

    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onSubmit',
        defaultValues: initialValues
    });

    const watchFromCreateDate = form.watch('fromCreatedDate');
    const watchToCreateDate = form.watch('toCreatedDate');
    const watchShopName = form.watch('shopName');
    const watchIsAllShop = form.watch('isAllShop');

    const onSearch = (searchParams: FormData) => {
        setSearchState(searchParams);
        onFetch(searchParams, defaultPaging);
    };

    useEffect(() => {
        loadDataSearch(watchShopName || '');
        loadDataTable();
    }, []);

    const loadDataSearch = async (name: string) => {
        await flowResult(getAllShopSearchPromotion({ name }));
    };

    const loadDataTable = async () => {
        await flowResult(getAllPromotion(searchState, paging));
    };

    const handleDisableTime = (date: Date, limitDate: string | Date | undefined, isToDate: boolean) => {
        if (limitDate) {
            return isToDate ? date < limitDate : date > limitDate;
        }
        return false;
    };

    const columns: ColumnDef<any>[] = [
        {
            header: t('words_title.index'),
            size: 10,
            enableSorting: false,
            cell: ({ row: { index } }) => (
                <div>{paging.pagination.pageIndex * paging.pagination.pageSize + index + 1}</div>
            )
        },
        {
            accessorKey: 'nameVn',
            header: t('words_title.promotion_name_lable'),
            enableSorting: false,
            size: 150,
            cell: ({ row: { original } }) => (
                <>
                    <div>{language === LANGUAGES_SUPPORTED.English ? original?.nameEn : original?.nameVn}</div>
                </>
            )
        },
        {
            accessorKey: 'promotionCode',
            header: t('words_title.promo_code_lable'),
            enableSorting: false,
            size: 180,
            cell: ({ row: { original } }) => (
                <div className='break-all'>
                    {original?.promotionCode ?? '--'}
                </div>
            )
        },
        {
            accessorKey: 'shopName',
            header: t('words_title.promotion_shop_name'),
            enableSorting: false,
            size: 250,
            cell: ({ row: { original } }) => (
                <>
                    {
                        original?.shop?.isDeleted === 0 ?
                            (
                                <Link to={`/shops/${original.shop.code}`} target='blank' rel='noopener noreferrer' className='underline text-link-1 cursor-pointer'>
                                    {original?.shop?.name ?? '--'}
                                </Link>
                            ) :
                            (
                                <div>{original?.shop?.name ?? '--'}</div>
                            )
                    }
                </>
            )
        },
        {
            accessorKey: 'quantity',
            header: t('words_title.quantity_voucher'),
            enableSorting: false,
            size: 120,
            cell: ({ row: { original } }) => (
                <div className='text-center'>{formatNumber(Number(original?.quantity))}</div>
            )
        },
        {
            accessorKey: 'perUserLimit',
            header: t('words_title.limits_of_use'),
            enableSorting: false,
            size: 150,
            cell: ({ row: { original } }) => (
                <div className='text-center'>{original?.perUserLimit}</div>
            )

        },
        {
            accessorKey: 'validityPeriod',
            header: t('words_title.validity_period'),
            enableSorting: false,
            size: 250,
            cell: ({ row: { original } }) => (
                <div>{`${original.startTime ? formatDateTime(original.startTime, 'DD/MM/YYYY') : '--'} - ${original.endTime ? formatDateTime(original.endTime, 'DD/MM/YYYY') : '--'}`}</div>
            )
        },
        {
            accessorKey: 'status',
            header: t('words_title.status'),
            enableSorting: false,
            size: 180,
            cell: ({ row: { original } }) => <>{getPromotionStatusLabel(original, t)}</>
        },
        {
            accessorKey: 'usageRate',
            header: t('words_title.usage_rate'),
            enableSorting: false,
            size: 150,
            cell: ({ row: { original } }) => (
                <div
                    className={`${original?.hasBeenUsed ? 'cursor-pointer text-[#0075FF] underline' : ''} `}
                    onClick={() => {
                        if (original?.hasBeenUsed) {
                            openPetOwnerListPopup(original);
                        }
                    }}
                >
                    {original.usageRate}
                </div>
            )
        },
        {
            accessorKey: 'createdAt',
            header: t('words_title.promotion_created_date'),
            enableSorting: false,
            size: 180,
            cell: ({ row: { original } }) => (
                <div>{original.createdAt ? formatDateTime(original.createdAt, 'DD/MM/YYYY  hh:mm A') : '--'}</div>
            )
        },
        {
            id: 'actions',
            size: 10,
            cell: ({ row: { original } }) => (
                <div className='flex justify-center items-center'>
                    <ActionMenuDropdown original={original} loadDataTable={loadDataTable} status={original?.statusLabel} />
                </div>
            )
        }
    ];

    const openPetOwnerListPopup = (original: any) => {
        showModal({
            size: '4xl',
            title: t('words_title.pet_owner_list'),
            contentClassName: 'px-0',
            content: (
                <PetOwnerListPopup promotionId={original?.id} />
            ),
            footerClassName: 'hidden'
        });
    };

    const onFetch = (searchParams?: any, paging?: TableState) => {
        if (paging) {
            setObservable('paging', paging ?? null);
        }

        setObservable('keepSearchParams', toJS(searchParams));
        getAllPromotion(searchParams, paging);
    };

    const onStateChange = (stateUpdater: Updater<TableState>) => {
        const newPaging = stateUpdater instanceof Function ? stateUpdater(paging) : stateUpdater;
        onFetch(searchState, newPaging);
        scrollToTop();
    };

    const handleClickAddPromotion = () => {
        navigate(ROUTES.promotionCreate.href);
    };

    return (
        <>
            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSearch)}>
                    <div className='bg-white rounded p-4'>
                        <div className='grid grid-cols-2 gap-x-20 gap-y-4'>
                            <FormField
                                control={form.control}
                                name='promotionName'
                                render={({ field }) => (
                                    <FormItem>
                                        <div className='flex items-center'>
                                            <Label className='w-1/5 label-1'>{t('words_title.promotion_name_lable')}</Label>
                                            <FormControl className='w-4/5'>
                                                <Input type='text' {...field} />
                                            </FormControl>
                                        </div>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name='promotionCode'
                                render={({ field }) => (
                                    <FormItem>
                                        <div className='flex items-center'>
                                            <Label className='w-1/5 label-1'>{t('words_title.promo_code_lable')}</Label>
                                            <FormControl className='w-4/5'>
                                                <Input type='text' {...field} />
                                            </FormControl>
                                        </div>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <div>
                                <FormField
                                    control={form.control}
                                    name='shopName'
                                    render={({ field }) => (
                                        <FormItem>
                                            <div className='flex items-center'>
                                                <Label className='w-1/5 label-1'>{t('words_title.promotion_shop_name')}</Label>
                                                <FormControl className='w-4/5'>
                                                    <Combobox
                                                        placeholder={t('placeholder.choose_promotion_shop')}
                                                        value={field.value ?? ''}
                                                        onChange={(item) => {
                                                            field.onChange(item?.value);
                                                            form.setValue('shopId', item?.code);
                                                        }}
                                                        items={
                                                            (Array.isArray(shopsSearchPromotion) ?
                                                                [
                                                                    ...shopsSearchPromotion.map(item => ({
                                                                        label: item?.name,
                                                                        value: item?.id,
                                                                        code: item?.id
                                                                    }))] :
                                                                []
                                                            )
                                                        }
                                                        side='top'
                                                        disabled={watchIsAllShop}
                                                        isButtonClear={true}
                                                    />
                                                </FormControl>
                                            </div>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <div className='flex items-center mt-4'>
                                    <Label className='w-1/5 label-1'></Label>
                                    <div className='w-4/5 flex items-center gap-8'>
                                        <FormField
                                            control={form.control}
                                            name='isAllShop'
                                            render={({ field }) => (
                                                <FormItem>
                                                    <div className='flex gap-2 items-center'>
                                                        <FormControl>
                                                            <Checkbox
                                                                checked={field.value}
                                                                onCheckedChange={field.onChange}
                                                            />
                                                        </FormControl>
                                                        <Label>{t('words_title.for_all_shops')}</Label>
                                                    </div>
                                                </FormItem>
                                            )}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className='flex items-start'>
                                <Label className='w-1/5 label-1 h-[48px] leading-[48px]'>{t('words_title.promotion_created_date')}</Label>
                                <div className='flex items-center gap-3 w-4/5'>
                                    <FormField
                                        control={form.control}
                                        name='fromCreatedDate'
                                        render={({ field }) => (
                                            <FormItem className='w-1/2'>
                                                <FormControl>
                                                    <Calendar
                                                        mode='single'
                                                        selected={field.value}
                                                        onSelect={field.onChange}
                                                        disabled={date => handleDisableTime(date, watchToCreateDate, false)}
                                                        useIconRemove={true}
                                                        className='w-full'
                                                        placeholder={t('placeholder.from')}
                                                        required
                                                    />
                                                </FormControl>
                                            </FormItem>
                                        )}
                                    />
                                    <div> - </div>
                                    <FormField
                                        control={form.control}
                                        name='toCreatedDate'
                                        render={({ field }) => (
                                            <FormItem className='w-1/2'>
                                                <FormControl>
                                                    <Calendar
                                                        mode='single'
                                                        selected={field.value}
                                                        onSelect={field.onChange}
                                                        disabled={date => handleDisableTime(date, watchFromCreateDate, true)}
                                                        useIconRemove={true}
                                                        className='w-full'
                                                        placeholder={t('placeholder.to')}
                                                        required
                                                    />
                                                </FormControl>
                                            </FormItem>
                                        )}
                                    />
                                </div>
                            </div>
                            <FormField
                                control={form.control}
                                name='status'
                                render={({ field }) => (
                                    <FormItem>
                                        <div className='flex items-center'>
                                            <Label className='w-1/5 label-1'>{t('words_title.status')}</Label>
                                            <Select
                                                key={field.value}
                                                onValueChange={field.onChange}
                                                {...field}
                                                className='w-4/5'
                                            >
                                                <FormControl>
                                                    <SelectTrigger>
                                                        <SelectValue placeholder={t('placeholder.choose')} />
                                                    </SelectTrigger>
                                                </FormControl>
                                                <SelectContent>
                                                    <SelectItem
                                                        className='hover:cursor-pointer'
                                                        value='all'
                                                    >
                                                        <span>{t('words_title.all')}</span>
                                                    </SelectItem>
                                                    <SelectItem
                                                        className='hover:cursor-pointer'
                                                        value={STATUS_PROMOTION.ACTIVE}
                                                    >
                                                        <span>{t('words_title.promotion_status_active')}</span>
                                                    </SelectItem>
                                                    <SelectItem
                                                        className='hover:cursor-pointer'
                                                        value={STATUS_PROMOTION.IN_ACTIVE}
                                                    >
                                                        <span>{t('words_title.promotion_status_inactive')}</span>
                                                    </SelectItem>
                                                    <SelectItem
                                                        className='hover:cursor-pointer'
                                                        value={STATUS_PROMOTION.EXPIRED}
                                                    >
                                                        <span>{t('words_title.promotion_status_expired')}</span>
                                                    </SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>

                        <div className='flex justify-center mt-8'>
                            <Button variant='outline' className='min-w-48 flex items-center justify-center gap-2 group'>
                                <SearchIcon className='w-5 h-5 text-active group-hover:text-white' />
                                <span>{t('button.search')}</span>
                            </Button>
                        </div>
                    </div>
                </form>
            </Form>

            <div className='bg-[#fff] p-4 mt-4 rounded'>
                <Button variant='default' className='text-white flex items-center justify-center gap-2 min-w-28 ml-auto' onClick={handleClickAddPromotion}>
                    <PlusIcon className='w-7 h-7 text-white' />
                    <span>{t('button.add_promotion')}</span>
                </Button>
                <Table
                    columns={columns}
                    data={promotions}
                    rowCount={totalRecord}
                    onStateChange={onStateChange}
                    state={paging}
                    selectPageSizes={DEFAULT_SELECT_PAGE_SIZES}
                    getRowId={row => row.id?.toString()}
                />
            </div>
        </>
    );
});

const getPromotionStatusLabel = (original: any, t: any) => {
    if (original?.statusLabel === STATUS_PROMOTION.EXPIRED) {
        return <div className='border-[1px] border-[#E40031] rounded-[16px] p-1 text-[#E40031] text-center min-w-[130px] max-w-[160px]'>{t('words_title.promotion_status_expired')}</div>;
    }

    if (original?.statusLabel === STATUS_PROMOTION.ACTIVE) {
        return <div className='border-[1px] border-[#009F46] rounded-[16px] p-1 text-[#009F46] text-center min-w-[130px] max-w-[160px]'>{t('words_title.promotion_status_active')}</div>;
    }

    if (original?.statusLabel === STATUS_PROMOTION.IN_ACTIVE) {
        return <div className='border-[1px] border-[#7D7D7D] rounded-[16px] p-1 text-[#7D7D7D] text-center min-w-[130px] max-w-[160px]'>{t('words_title.promotion_status_inactive')}</div>;
    }

    return <div>--</div>;
};

const STATUS_PROMOTION = {
    ACTIVE: 'active',
    IN_ACTIVE: 'in_active',
    EXPIRED: 'expired'
};

interface ActionMenuDropdownProps {
    status: string,
    loadDataTable: () => void,
    original: any
}

const ActionMenuDropdown = observer(({ original, loadDataTable, status }: ActionMenuDropdownProps) => {
    // hooks
    const { t } = useTranslation();
    const navigate = useNavigate();

    const {
        modalStore: { showAlertModal, modalWaringAlertOptions, hideModal },
        promotionStore: { deletePromotion, changeStatusPromotion }
    } = useStore();

    // // function
    const handleClickInActive = async () => {
        showAlertModal(modalWaringAlertOptions({
            hideIconCenter: true,
            size: 'lg',
            content: t('messages.confirm_inactive_promotion'),
            onSave: async () => {
                const res = await flowResult(changeStatusPromotion(original.id, { status: false }));
                if (res && res.ok) {
                    loadDataTable();
                    toastify('success', t('messages.update_success'));
                }
            }
        }));
    };

    const handleClickActive = async () => {
        showAlertModal(modalWaringAlertOptions({
            hideIconCenter: true,
            size: 'lg',
            content: t('messages.confirm_active_promotion'),
            onSave: async () => {
                const res = await flowResult(changeStatusPromotion(original.id, { status: true }));
                if (res && res.ok) {
                    loadDataTable();
                    toastify('success', t('messages.update_success'));
                }
            }
        }));
    };

    const handleClickDelete = async () => {
        showAlertModal(modalWaringAlertOptions({
            hideIconCenter: true,
            size: 'lg',
            content: t('messages.confirm_delete_promotion'),
            onSave: async () => {
                const res = await flowResult(deletePromotion(original?.id));
                if (res) {
                    toastify('success', t('messages.update_success'));
                    loadDataTable();
                }
            }
        }));
    };

    const handleClickClone = async () => {
        navigate(`${ROUTES.promotionClone.href}/${original?.id}`);
    };

    const handleClickEdit = async () => {
        navigate(`${ROUTES.promotionEdit.href}/${original?.id}`);
    };

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <EllipsisIcon className='h-6 w-6 text-active hover:cursor-pointer' />
            </DropdownMenuTrigger>

            <DropdownMenuContent className='w-48' align='end' forceMount>

                {
                    status === STATUS_PROMOTION.ACTIVE && (
                        <>
                            <DropdownMenuItem className='hover:cursor-pointer' asChild>
                                <ActionDropdownItem icon={Ban} text={t('words_title.inactive')} onClick={handleClickInActive} />
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                        </>
                    )
                }

                {
                    status === STATUS_PROMOTION.IN_ACTIVE && (
                        <>
                            <DropdownMenuItem className='hover:cursor-pointer' asChild>
                                <ActionDropdownItem icon={Check} text={t('words_title.active')} onClick={handleClickActive} />
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                        </>
                    )
                }

                <DropdownMenuItem className='hover:cursor-pointer' asChild>
                    <ActionDropdownItem icon={Copy} text={t('words_title.inactive_clone')} onClick={handleClickClone} />
                </DropdownMenuItem>
                <DropdownMenuSeparator />

                <DropdownMenuItem className='hover:cursor-pointer' asChild>
                    <ActionDropdownItem icon={Edit} text={t('words_title.edit_action')} onClick={handleClickEdit} />
                </DropdownMenuItem>
                <DropdownMenuSeparator />

                <DropdownMenuItem className='hover:cursor-pointer' asChild>
                    <ActionDropdownItem icon={Trash2Icon} text={t('words_title.delete_action')} onClick={handleClickDelete} />
                </DropdownMenuItem>

            </DropdownMenuContent>
        </DropdownMenu>
    );
});
